import { RefObject, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import Career from 'wwg-utilities/dist/collections/career';
import CareerConnection from 'wwg-utilities/dist/collections/career-connection';
import CareerSeeker from 'wwg-utilities/dist/collections/career-seeker';
import MyListMessage from '../../components/shared/MyListMessage';
import createCareerConnectionRequest from '../api/createCareerConnectionRequeset';
import deleteCareerConnectionRequest from '../api/deleteCareerConnectionRequest';
import getCareerConnectionRequest from '../api/getCareerConnectionRequest';
import { isSkillup } from '../config/project/project';
import {
  addOneHourCookie,
  CAREER_TO_ADD,
  getCookie,
  removeCookie,
} from '../cookies';

import { AuthType } from '../enums';
import { useAuth } from '../providers/authProvider';
import { useAuthType } from '../providers/authTypeState';
import { useRegion } from '../providers/regionProvider';
import gaDataLayerEvent from './GA/gaDataLayerEvent';

const useCareerConnection = (
  career: Career,
  seeker: CareerSeeker | null,
  careerRef?: RefObject<HTMLDivElement | null>,
  shouldFadeOnDelete?: boolean,
) => {
  // This holds the state for whether or not the user has a career connection
  const [connection, setConnection] = useState<CareerConnection | null>(null);
  const profileUrl = useRef(`/profile/my-careers`);
  // This is a custom state provider that allows us to open and close the login/register modal
  const { setAuthType } = useAuthType();
  const { updateSeekerTasks } = useAuth();
  const { region } = useRegion();

  // This accesses the API to create a CareerConnection object in the DB
  const addCareerConnection = async (seeker: CareerSeeker, career: Career) => {
    try {
      const { connection, task } = await createCareerConnectionRequest(
        seeker.id,
        career.id,
        `${seeker?.name} <> ${career.title}`,
        region.name,
      );
      if (isSkillup) {
        updateSeekerTasks(task);
      }
      // If it's successful, we're setting the state for the the connection
      setConnection(connection);
      // and we're letting the user know with an alert
      toast(
        MyListMessage({
          message: `${career.title} successfully added to`,
          url: profileUrl.current,
        }),
        {
          type: 'success',
        },
      );
      gaDataLayerEvent({
        event: 'item_saved',
        item_type: 'career',
        item_name: career.title,
      });
    } catch {
      // If it's unsucessful, we're letting the user nkow with an alert
      toast(
        MyListMessage({
          message: `Error adding ${career.title} to`,
          url: profileUrl.current,
        }),
        {
          type: 'error',
        },
      );
    }
  };

  // This called the API to remove the CareerConnection oject from the DB
  const removeCareerConnection = async (careerId: string) => {
    try {
      const { task } = await deleteCareerConnectionRequest(careerId);
      if (isSkillup) {
        updateSeekerTasks(task);
      }

      // If we're successful, clear out the connection
      setConnection(null);
      // and we let the user know it was successful
      toast(
        MyListMessage({
          message: `${career.title} successfully removed from`,
          url: profileUrl.current,
        }),
        {
          type: 'success',
        },
      );
      if (shouldFadeOnDelete) {
        careerRef?.current?.classList.add('animate-fade-out-2');
        setTimeout(() => {
          careerRef?.current?.classList.add('hidden');
        }, 750);
      }
    } catch {
      // If it's not successful, we let the user know
      toast(
        MyListMessage({
          message: `Error removing ${career.title} from`,
          url: profileUrl.current,
        }),
        {
          type: 'error',
        },
      );
    }
  };
  const handleClick = () => {
    // If the user is not logged in,
    // Show the login form
    if (!seeker) {
      setAuthType(AuthType.Login);
      addOneHourCookie(CAREER_TO_ADD, career.id);
      return;
    }

    // If the user is logged in, and
    // there is no connection,
    // add a connection
    if (!connection) {
      addCareerConnection(seeker as CareerSeeker, career);
    }

    // If the user is logged in, and there is a connection
    // remove the connection
    if (connection) {
      removeCareerConnection(connection.id);
    }
  };

  // This listens for changes to the career and seeker object
  // to reinitialize the connection. Mostly this is useful for the
  // first render to initially set the connection
  useEffect(() => {
    if (career && seeker) {
      getAndSetConnection(career, seeker);
    } else {
      setConnection(null);
    }
    return () => setConnection(null);
  }, [seeker?.id]);

  // This accesses the API to pull the connection
  const getAndSetConnection = async (career: Career, seeker: CareerSeeker) => {
    const response = await getCareerConnectionRequest(seeker?.id, career.id);

    setConnection(response || null);
    const careerToAdd = getCookie(CAREER_TO_ADD);
    if (careerToAdd == career.id) {
      if (!response) {
        removeCookie(CAREER_TO_ADD);
        await addCareerConnection(seeker, career);
      }
    }

    return Promise.resolve((response as CareerConnection) || null);
  };

  return {
    connection,
    handleClick,
  };
};

export default useCareerConnection;
